import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useCmsPage, c } from '../../lib/cms';

const jsonLd = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://xentrios.com' },
        { '@type': 'ListItem', position: 2, name: 'Compare', item: 'https://xentrios.com/compare' },
        {
          '@type': 'ListItem',
          position: 3,
          name: 'NDS vs Kolla',
          item: 'https://xentrios.com/compare/nds-vs-kolla',
        },
      ],
    },
    {
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What is the difference between NDS DataVault and Kolla?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Kolla focuses on PMS data extraction and normalization — it provides API-based connectors to dental PMS platforms and delivers clean data for downstream analytics tools. NDS DataVault provides a fuller data stack: PMS extraction, normalization, a managed data warehouse, analytics delivery (including Power BI), and a direct integration path to RevTech RCM products. DataVault is designed as an end-to-end dental data infrastructure layer, not just a connector.',
          },
        },
        {
          '@type': 'Question',
          name: 'Does NDS DataVault connect to as many PMS platforms as Kolla?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'NDS DataVault supports connectors for Dentrix, Eaglesoft, Open Dental, CareStack, Planet DDS, Denticon, and Xentrios, among others. NDS has specialized in U.S. dental PMS integration for over two decades, which means its connectors go deep — capturing not just production data but also claim-level, payment-level, and patient-level data needed for RCM analytics. If your PMS connector list is the primary decision criterion, you should evaluate specific connector support for your current and anticipated future PMS platforms with both vendors.',
          },
        },
        {
          '@type': 'Question',
          name: 'Can Kolla connect to RevTech products?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Kolla is a data connector platform — it extracts and normalizes PMS data and delivers it to your chosen analytics tools. RevTech products are designed to receive data from NDS DataVault natively. While it is theoretically possible to pipe data from Kolla into RevTech tools via custom integration, that is not a supported or pre-built integration path. Groups choosing RevTech products typically use NDS DataVault as the integration layer because it is the native data source.',
          },
        },
        {
          '@type': 'Question',
          name: 'Does NDS offer managed IT or data services beyond the DataVault product?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. National Data Solutions offers managed IT services for dental groups in addition to DataVault. This includes network management, infrastructure support, and technology advisory services for DSOs that want a single managed services vendor for their technology stack. Kolla is a data product company and does not offer managed IT services.',
          },
        },
      ],
    },
  ],
};

const tableRows = [
  {
    criterion: 'PMS connectors supported',
    nds: { value: 'Dentrix, Eaglesoft, Open Dental, CareStack, Planet DDS, Denticon, Xentrios — deep data extraction at claim, payment, and patient levels', check: true },
    competitor: { value: 'Broad connector support across major dental PMS platforms; depth varies by connector', check: true },
  },
  {
    criterion: 'Data normalization depth',
    nds: { value: 'Full normalization including CDT codes, payer identifiers, claim-level fields, and payment transactions', check: true },
    competitor: { value: 'Normalization focused on production and patient data; claim/payment depth is more limited', check: false },
  },
  {
    criterion: 'Analytics layer',
    nds: { value: 'Managed data warehouse with Power BI integration, custom data models, and direct feed to RevTech analytics', check: true },
    competitor: { value: 'Data delivery to your chosen BI tool; no managed analytics layer included', check: false },
  },
  {
    criterion: 'RCM integration',
    nds: { value: 'Native integration with InsightFlow, DenialIQ, ClaimVision, PayerIQ, and ReconIQ — no custom development needed', check: true },
    competitor: { value: 'Not a supported integration path for RevTech products', check: false },
  },
  {
    criterion: 'Real-time vs. batch sync',
    nds: { value: 'Near-real-time sync available for supported PMS platforms; batch available for all', check: true },
    competitor: { value: 'Sync frequency varies by PMS connector and plan tier', check: false },
  },
  {
    criterion: 'Custom data models',
    nds: { value: 'Custom schemas available for enterprise DSOs; NDS data team handles model design and maintenance', check: true },
    competitor: { value: 'Standard normalized schema; custom modeling requires your internal data team', check: false },
  },
  {
    criterion: 'Managed IT services',
    nds: { value: 'Full managed IT services available for dental groups — network, infrastructure, and tech advisory', check: true },
    competitor: { value: 'Not offered — data product company only', check: false },
  },
  {
    criterion: 'Pricing',
    nds: { value: 'Enterprise pricing by location count and product tier; DataVault + RevTech bundle pricing available', check: true },
    competitor: { value: 'SaaS subscription; pricing varies by connector count and data volume', check: true },
  },
  {
    criterion: 'U.S. DSO focus',
    nds: { value: 'Purpose-built for U.S. dental groups — U.S. payer logic, CDT codes, and DSO-specific data models native', check: true },
    competitor: { value: 'U.S. dental is a core market; international markets also served', check: true },
  },
];

export default function NDSVsKollaPage() {
  const cmsPage = useCmsPage('xentrios', 'nds-vs-kolla');
  const heroHeadline = c(cmsPage, 'hero', 'headline', 'NDS DataVault vs. Kolla');
  const heroSubtitle = c(cmsPage, 'hero', 'subtitle', 'Both platforms serve the dental data infrastructure category — extracting PMS data and making it available for analytics. The difference is in depth: Kolla provides connectivity; NDS DataVault provides the full data stack from extraction through managed warehouse to RCM analytics delivery.');
  const tableRowItems = c(cmsPage, 'features', 'items', tableRows);
  return (
    <div>
      <Helmet>
        <title>NDS vs Kolla — Dental Data Warehouse Comparison</title>
        <meta
          name="description"
          content="Compare National Data Solutions (NDS DataVault) vs. Kolla for dental data warehousing. Compare PMS connector breadth, analytics layer, RCM integration, and DSO scalability."
        />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* Hero */}
      <section className="xen-hero">
        <div className="xen-container">
          <p className="xen-hero-eyebrow">Platform Comparison</p>
          <h1>{heroHeadline}</h1>
          <p className="subtitle">{heroSubtitle}</p>
          <div className="xen-hero-actions">
            <Link to="/demo" className="xen-btn xen-btn-primary" style={{ padding: '0.75rem 2rem', fontSize: '1rem' }}>
              See NDS DataVault in Action
            </Link>
            <Link
              to="/stack"
              className="xen-btn xen-btn-outline"
              style={{ padding: '0.75rem 2rem', fontSize: '1rem', borderColor: '#fff', color: '#fff' }}
            >
              Explore the Full Stack
            </Link>
          </div>
        </div>
      </section>

      {/* Intro framing */}
      <section className="xen-section xen-section-light">
        <div className="xen-container">
          <p className="xen-section-eyebrow">Market Context</p>
          <h2>Dental data infrastructure: connectivity vs. a full data stack.</h2>
          <p className="xen-section-subtitle" style={{ marginBottom: '2rem' }}>
            As DSOs grow, the fragmentation of data across multiple PMS instances, clearinghouses, and billing
            systems becomes an operational constraint. Both Kolla and NDS DataVault address this problem, but
            from different architectural positions.
          </p>
          <div className="xen-two-col" style={{ gap: '3rem' }}>
            <div style={{ background: '#f8f9fc', borderRadius: '12px', padding: '2rem' }}>
              <h3 style={{ color: 'var(--xen-navy)', marginBottom: '1rem' }}>Kolla</h3>
              <p>
                Kolla provides API connectors to dental PMS platforms and delivers normalized data to downstream
                analytics tools. It is a developer-friendly data extraction and normalization layer — well-suited
                for DSOs that have in-house data teams capable of building and maintaining analytics
                infrastructure on top of the connector layer. Kolla's value is in the breadth of connectors
                and the speed with which clean, normalized PMS data can be available to your analytics tools.
              </p>
              <p style={{ marginTop: '1rem' }}>
                Kolla does not provide a managed analytics layer, a data warehouse product, or an integration
                path to purpose-built dental RCM products. You bring the BI tooling and the data engineering
                capacity; Kolla provides the pipe.
              </p>
            </div>
            <div style={{ background: '#f0f4ff', borderRadius: '12px', padding: '2rem' }}>
              <h3 style={{ color: 'var(--xen-navy)', marginBottom: '1rem' }}>NDS DataVault</h3>
              <p>
                NDS DataVault is a managed dental data warehouse. National Data Solutions handles the full
                data pipeline: PMS connectors, ETL, normalization, warehouse management, and analytics delivery.
                DataVault is built specifically to feed RevTech RCM products — InsightFlow, DenialIQ, ClaimVision,
                PayerIQ, and ReconIQ — natively, without additional integration work.
              </p>
              <p style={{ marginTop: '1rem' }}>
                For DSOs that do not have a dedicated data engineering team, DataVault provides the managed
                infrastructure that replaces the in-house build. For DSOs using RevTech analytics, DataVault
                is the required data layer — it is the foundation on which RevTech products operate.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison table */}
      <section className="xen-section" style={{ background: '#fff' }}>
        <div className="xen-container">
          <p className="xen-section-eyebrow">Side-by-Side Comparison</p>
          <h2>NDS DataVault vs. Kolla — Feature Comparison</h2>
          <p className="xen-section-subtitle" style={{ marginBottom: '2rem' }}>
            Evaluated across 9 criteria relevant to DSO data infrastructure decisions.
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.95rem', minWidth: '640px' }}>
              <thead>
                <tr style={{ background: 'var(--xen-navy)', color: '#fff' }}>
                  <th style={{ padding: '0.85rem 1.1rem', textAlign: 'left', fontWeight: 600 }}>Criterion</th>
                  <th style={{ padding: '0.85rem 1.1rem', textAlign: 'left', fontWeight: 600 }}>NDS DataVault</th>
                  <th style={{ padding: '0.85rem 1.1rem', textAlign: 'left', fontWeight: 600 }}>Kolla</th>
                </tr>
              </thead>
              <tbody>
                {tableRowItems.map((row, i) => (
                  <tr key={row.criterion} style={{ background: i % 2 === 0 ? '#f8f9fc' : '#fff' }}>
                    <td style={{ padding: '0.85rem 1.1rem', fontWeight: 600, color: 'var(--xen-navy)', verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                      {row.criterion}
                    </td>
                    <td style={{ padding: '0.85rem 1.1rem', color: '#333', verticalAlign: 'top' }}>
                      <span style={{ marginRight: '0.4rem', color: row.nds.check ? '#00897b' : '#e53935', fontWeight: 700 }}>
                        {row.nds.check ? '✓' : '✗'}
                      </span>
                      {row.nds.value}
                    </td>
                    <td style={{ padding: '0.85rem 1.1rem', color: '#333', verticalAlign: 'top' }}>
                      <span style={{ marginRight: '0.4rem', color: row.competitor.check ? '#00897b' : '#e53935', fontWeight: 700 }}>
                        {row.competitor.check ? '✓' : '✗'}
                      </span>
                      {row.competitor.value}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Verdict */}
      <section className="xen-section xen-section-light">
        <div className="xen-container">
          <p className="xen-section-eyebrow">Verdict</p>
          <h2>Which dental data infrastructure fits your group?</h2>
          <div className="xen-two-col" style={{ gap: '2.5rem', marginTop: '2rem' }}>
            <div style={{ background: '#fff', border: '2px solid #e0e4f0', borderRadius: '12px', padding: '2rem' }}>
              <h3 style={{ color: 'var(--xen-navy)', marginBottom: '1rem' }}>Choose Kolla if…</h3>
              <ul style={{ paddingLeft: '1.25rem', lineHeight: 2, color: '#444' }}>
                <li>You have an in-house data engineering team to build on top of the connector layer</li>
                <li>You want to choose your own BI tools and analytics infrastructure independently</li>
                <li>Connector breadth across a specific PMS mix is the primary evaluation criterion</li>
                <li>You need developer-facing API access to dental PMS data for custom application development</li>
                <li>You are not planning to use RevTech RCM analytics products</li>
              </ul>
            </div>
            <div style={{ background: '#f0f4ff', border: '2px solid var(--xen-accent)', borderRadius: '12px', padding: '2rem' }}>
              <h3 style={{ color: 'var(--xen-navy)', marginBottom: '1rem' }}>Choose NDS DataVault if…</h3>
              <ul style={{ paddingLeft: '1.25rem', lineHeight: 2, color: '#444' }}>
                <li>You want a fully managed data warehouse without internal data engineering overhead</li>
                <li>You plan to use RevTech RCM products — DataVault is the native data layer for the stack</li>
                <li>Revenue cycle data (claims, denials, payments) at the claim-line level is required</li>
                <li>You want Power BI delivery alongside RCM analytics in a single managed infrastructure</li>
                <li>Managed IT services for the broader technology stack are also a priority</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="xen-section" style={{ background: '#fff' }}>
        <div className="xen-container">
          <p className="xen-section-eyebrow">FAQ</p>
          <h2>Common questions about NDS DataVault vs. Kolla</h2>
          <div style={{ marginTop: '2rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {jsonLd['@graph'][1].mainEntity.map((faq) => (
              <div key={faq.name} style={{ background: '#f8f9fc', borderRadius: '10px', padding: '1.5rem 2rem' }}>
                <h3 style={{ color: 'var(--xen-navy)', marginBottom: '0.5rem', fontSize: '1.05rem' }}>{faq.name}</h3>
                <p style={{ color: '#444', lineHeight: 1.75 }}>{faq.acceptedAnswer.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="xen-section xen-section-dark xen-section-center">
        <div className="xen-container">
          <h2>Build your dental data foundation with NDS DataVault.</h2>
          <p className="xen-section-subtitle" style={{ marginTop: '1rem', marginBottom: '2rem' }}>
            Talk to a solutions engineer about your PMS environment, current reporting gaps, and analytics goals.
            We'll show you how DataVault supports both your BI needs and the full RevTech RCM product suite.
          </p>
          <Link to="/demo" className="xen-btn xen-btn-primary" style={{ padding: '0.875rem 2.5rem', fontSize: '1.05rem' }}>
            Request a Demo
          </Link>
        </div>
      </section>
    </div>
  );
}
